import { useQuery } from "@tanstack/react-query";
import type { ProductDto } from "@gaole/shared";
import { api } from "../lib/api";
import { rm } from "../lib/format";
import { ProductImage } from "../components/ProductCard";
import { AuctionCard } from "../components/storefront/AuctionCard";
import { PageHeader } from "../components/storefront/PageHeader";
import { HorizontalScrollSection, ScrollCard } from "../components/storefront/HorizontalScrollSection";
import { Badge, EmptyState, PageLoader, statusColor } from "../components/ui";
import { Gavel } from "lucide-react";
import { Link } from "react-router-dom";

interface AuctionListItem {
  id: string;
  title: string;
  status: string;
  currentPrice: number;
  startPrice: number;
  bidCount: number;
  endsAt: string;
  startsAt: string;
  isLeading: boolean;
  product: ProductDto;
}

export function Auctions() {
  const { data, isLoading } = useQuery({
    queryKey: ["auctions"],
    queryFn: () => api<{ items: AuctionListItem[] }>("/auctions"),
    refetchInterval: 30_000,
  });

  if (isLoading) return <PageLoader />;

  const live = data?.items.filter((a) => ["LIVE", "SCHEDULED"].includes(a.status)) ?? [];
  const ended = data?.items.filter((a) => ["ENDED", "COMPLETED"].includes(a.status)) ?? [];

  return (
    <div className="min-w-0 max-w-full space-y-5 px-4 pt-4 lg:px-6 lg:flex lg:gap-8">
      <div className="min-w-0 flex-1 space-y-5">
        <PageHeader
          title="Auctions"
          description="Bid on grails with proxy bidding — we auto-raise up to your max until you're outbid."
        />

        {live.length === 0 && ended.length === 0 && (
          <EmptyState icon={Gavel} title="No auctions right now" hint="New lots go live regularly." />
        )}

        {live.length > 0 && (
          <>
            <div className="lg:hidden">
              <HorizontalScrollSection title="Live now">
                {live.map((auction) => (
                  <ScrollCard key={auction.id} className="w-[85vw] sm:w-80">
                    <AuctionCard auction={auction} />
                  </ScrollCard>
                ))}
              </HorizontalScrollSection>
            </div>

            <section className="hidden space-y-2 lg:block">
              <h2 className="font-display text-xs font-semibold uppercase tracking-widest text-white/40">Live now</h2>
              {live.map((auction) => (
                <AuctionCard key={auction.id} auction={auction} />
              ))}
            </section>
          </>
        )}

        {ended.length > 0 && (
          <section className="space-y-2">
            <h2 className="font-display text-xs font-semibold uppercase tracking-widest text-white/40">Ended</h2>
            {ended.slice(0, 6).map((auction) => (
              <Link
                key={auction.id}
                to={`/auctions/${auction.id}`}
                className="flex items-center gap-3 rounded-2xl border border-white/5 bg-surface/60 p-3 transition-colors hover:border-white/10"
              >
                <ProductImage src={auction.product.images[0] ?? null} alt="" className="h-11 w-11 shrink-0 rounded-xl" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs text-white/80">{auction.title}</p>
                  <p className="text-[10px] text-white/45">
                    {auction.bidCount > 0 ? `Sold for ${rm(auction.currentPrice)}` : "No bids"}
                  </p>
                </div>
                <Badge color={statusColor(auction.status)}>{auction.status}</Badge>
              </Link>
            ))}
          </section>
        )}
      </div>
    </div>
  );
}
